import { QueryClientProvider } from '@tanstack/react-query';
import { Outlet, useLocation, useNavigate } from '@tanstack/react-router';
import { useEffect, useMemo } from 'react';
import { SessionGate, useSession } from '@/components/auth/session';
import { DensityProvider } from '@/components/layout/DensityProvider';
import { Sidebar } from '@/components/layout/Sidebar';
import { ShellProvider, useShellKeys } from '@/components/layout/shell';
import { ThemeProvider } from '@/components/layout/ThemeProvider';
import { Toaster } from '@/components/ui/sonner';
import { BriefComposer } from '@/features/briefs/BriefComposer';
import { CommandPalette } from '@/features/command/CommandPalette';
import { useActivityFeed, useMyActivityFeed } from '@/hooks/use-activity-feed';
import { type FleetFeed, FleetFeedContext } from '@/lib/feed-context';
import { queryClient } from '@/lib/query';

/**
 * The stream lives here, above the router, for one reason: `EventSource` is a
 * connection, and remounting a route must not drop and re-open it. The feed and
 * the connection state flow down through context; the query invalidation the
 * stream triggers flows through the query client instead.
 *
 * Which stream is opened depends on the role, and only on the role. The org-wide
 * journal is the whole company's activity and stays an admin's (DASHBOARDS-PLAN.md
 * phase A); a member opens `/api/me/stream`, which the hub scopes in SQL to the
 * devices they own and the briefs they filed. Exactly one is ever open, so a
 * member's page is as live as an admin's without either of them reading the
 * other's events.
 *
 * The shell is a fixed rail and a scrolling column: the rail is `position:
 * fixed` rather than a grid track so it never scrolls with the page and never
 * re-lays-out when a long list changes height. Below `lg` it becomes a sheet and
 * the column takes the whole width.
 */
function AppShell() {
  const { session } = useSession();
  usePasswordGate(session.mustChangePassword);
  // While the password is temporary the hub answers 403 to both streams, so
  // neither is opened: a stream that is refused every reconnect is noise in the
  // rail and in the hub's log, and the account page needs no live feed.
  const fleet = useActivityFeed(session.isAdmin && !session.mustChangePassword);
  const mine = useMyActivityFeed(!session.isAdmin && !session.mustChangePassword);
  const wire = session.isAdmin ? fleet : mine;
  const feed = useMemo<FleetFeed>(
    () => ({ items: wire.items, scope: session.isAdmin ? 'fleet' : 'mine' }),
    [session.isAdmin, wire.items],
  );
  useShellKeys();

  return (
    <div className="dy-floor relative min-h-screen">
      {/* The page's weather, behind everything and under nothing's pointer. It is
          the brand gradient at single-digit alpha, blurred across the whole
          viewport, and it is what stops a console of white cards from reading as
          a spreadsheet. See `.dy-aurora`. */}
      <div className="dy-aurora" aria-hidden />
      <Sidebar live={wire.live} />
      {/* 248px, which is what the rail is. It was 232 here and 248 there, so
          every page sat 16px under the rail's own edge and the content's left
          margin was a different number on every screen wide enough to show
          both. */}
      <div className="relative z-[1] min-w-0 lg:pl-[248px]">
        <FleetFeedContext.Provider value={feed}>
          <Outlet />
        </FleetFeedContext.Provider>
      </div>
      <CommandPalette />
      {/* One instance for the whole app rather than one per route that draws a
          trigger (issue #89): it opens over whatever page is open instead of
          sending the person to Jobs first, and mounting it once here, rather
          than inside the Jobs route, is what lets a draft survive being closed
          and reopened in the same session - the fields live in this
          component's own state, and this component never unmounts on
          navigation. */}
      <BriefComposer />
    </div>
  );
}

/**
 * A temporary password is a session with one page in it (docs/JOB-LIFECYCLE.md
 * section 2.9). The hub refuses every `/api` route but `/api/me` until it is
 * changed, so any other page would draw as a wall of refusals; the person is
 * sent to the account page, where the change-password card is open and says
 * why. Sign-out stays reachable from the rail. Nothing here decides what the
 * person may do; the hub already has, and this only draws the answer.
 */
function usePasswordGate(mustChangePassword: boolean) {
  const location = useLocation();
  const navigate = useNavigate();
  useEffect(() => {
    if (!mustChangePassword || location.pathname === '/me') return;
    void navigate({ to: '/me', replace: true });
  }, [mustChangePassword, location.pathname, navigate]);
}

export function RootLayout() {
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider>
        <DensityProvider>
          <ShellProvider>
            <SessionGate>
              <AppShell />
            </SessionGate>
            <Toaster />
          </ShellProvider>
        </DensityProvider>
      </ThemeProvider>
    </QueryClientProvider>
  );
}
